/**
 * Istanbul's Kız Kulesi — the Maiden's Tower — in the shared 120×120 LINE asset box.
 *
 * TRACED from the theme's own icon, not invented: the tower standing in the
 * Bosphorus at the bottom-right of istanbul-theme/icon.png. Its parts were
 * measured off that art column by column (shaft 153px wide, balcony 205, base
 * 435, ground at 655) and rescaled into this box, so the PROPORTIONS are the
 * icon's even where the drawing is simplified.
 *
 * WHAT IS LOAD-BEARING — do not "clean this up":
 *
 *  - The BASE IS OFF-CENTRE. The shaft stands at roughly a quarter across the
 *    base's width, with the long wing of the building running off to the right.
 *    Centring the shaft is the single fastest way to turn this into a generic
 *    lighthouse; the lopsided plan is what the real thing looks like from the
 *    Üsküdar shore and what the icon draws.
 *  - The BALCONY COLLAR IS WIDER THAN THE SHAFT. It is the only place the
 *    silhouette steps outward between the base and the cap, and at tab size it
 *    is the detail that says "tower" instead of "chimney". It is drawn thicker
 *    here than the icon's hairline band on purpose — at 22px the icon's true
 *    2.8-unit band is half a pixel and disappears.
 *  - The CAP IS BULBOUS WITH A KNOB ON TOP, not a cone. The icon's cap swells
 *    outward at its foot, pinches to a waist and carries a small drum finial
 *    above the waist. A plain triangle reads as a fir tree at small sizes.
 *  - The CAP'S FOOT OVERHANGS THE GALLERY. This one is NOT in the icon, where
 *    cap and gallery are the same width — it is the fix for the 22px gate. Drawn
 *    flush, the cap, the gallery and the shaft stack into one column and the
 *    whole thing reads as a bottle; five units of overhang per side is what turns
 *    the top back into a hat. Flattening it is the change that fails the gate.
 *  - The ROOF SITS ON THE RIGHT WING ONLY and its cornice band runs the FULL
 *    width of the base. That red line under the roof is what keeps the base from
 *    reading as a blank plinth once the walls go pale.
 *
 * The same paths serve the Home tab glyph AND the passcode dots, so everything
 * here has to survive being scaled right down: no stroke-only detail, no shape
 * thinner than ~2.5 units, and every part overlaps its neighbour so the OFF
 * silhouette unions without seams ([[line-shared-glyph-modules]]). Every subpath
 * is wound the same way for that union — measured, not assumed: all ten come out
 * with the same sign, so nonzero fill can never punch one of them into a hole.
 *
 * The fit, measured off the paths themselves: ink spans y 9 → 111.5 (85% of the
 * long axis) and x 15.5 → 103 (73% of the short one), centred on (59.3, 60.3).
 * The box is deliberately not filled sideways — the tower is a tall thing and
 * the air either side of the base is what says so.
 *
 * ON  = the lit tower, four tones (stone, ink, roof red, flag red).
 * OFF = MAIDEN_SOLID, every part in ONE flat muted tone, drawn as a single path
 *       with nonzero fill so the overlaps merge into one silhouette.
 *
 * DRAW ORDER for the ON state — the parts overlap, so it matters:
 *   BASE, SHAFT, GALLERY   in MAIDEN_STONE
 *   ROOF                   in MAIDEN_ROOF_TILE
 *   COLLAR, CAP, POLE      in MAIDEN_INK
 *   FLAG                   in MAIDEN_FLAG_RED
 *   WINDOWS                in MAIDEN_INK, ~0.72 opacity (they are shadow, not ink)
 */

const f = ( v: number ) => +v.toFixed( 1 );

/** A clockwise rectangle — clockwise so every subpath of MAIDEN_SOLID winds the
 *  same way and nonzero fill unions them instead of punching holes. */
function box( x0: number, y0: number, x1: number, y1: number ): string {
  return `M${ f( x0 ) },${ f( y1 ) } L${ f( x0 ) },${ f( y0 ) }`
    + ` L${ f( x1 ) },${ f( y0 ) } L${ f( x1 ) },${ f( y1 ) } Z`;
}

/** A clockwise trapezoid: a top edge and a bottom edge, each given as a span. */
function trap( y0: number, tl: number, tr: number, y1: number, bl: number, br: number ): string {
  return `M${ f( bl ) },${ f( y1 ) } L${ f( tl ) },${ f( y0 ) }`
    + ` L${ f( tr ) },${ f( y0 ) } L${ f( br ) },${ f( y1 ) } Z`;
}

/* ---------------------------------------------------------------- geometry --
 * All of it hangs off two numbers: where the shaft's axis is, and where the
 * water line is. Everything else is measured from the icon in units of those.
 */

/** The shaft's axis. LEFT of the box centre — see the off-centre note above. */
const SX = 40;
/** The waterline the whole thing sits on. */
const GROUND = 111.5;
/** The base building's roofline and its two ends. */
const BASE_TOP = 88;
const BASE_L = 15.5;
const BASE_R = 100;

/* --------------------------------------------------------------- the parts -- */

/** The low wide base building, walls only — the cornice band above it belongs to
 *  the roof path, because they are the same red. */
export const MAIDEN_BASE = box( BASE_L, BASE_TOP + 3, BASE_R, GROUND );

/** The tiled roof over the right wing, plus the cornice that runs the full width
 *  of the base under it. One path: both are the same dark red in the icon. */
export const MAIDEN_ROOF = trap( 73.5, 57.5, 96.5, BASE_TOP + 0.3, 53.5, 103 )
  + ' ' + box( BASE_L, BASE_TOP, BASE_R, BASE_TOP + 3.4 );

/** The cylindrical shaft, from under the balcony down into the base. It runs
 *  past the base's roofline on purpose so the union has no seam. */
export const MAIDEN_SHAFT = box( SX - 11.8, 56, SX + 11.8, BASE_TOP + 4 );

/** The gallery above the balcony: a touch wider than the shaft below it, which
 *  is what makes the collar read as a balcony rather than a belt. */
export const MAIDEN_GALLERY = box( SX - 13, 39.5, SX + 13, 51 );

/** The balcony collar — the widest thing between base and cap. */
export const MAIDEN_COLLAR = box( SX - 19.5, 49.5, SX + 19.5, 57 );

/**
 * The cap: a bulbous cone that swells at the foot, pinches to a waist, and
 * carries a small drum finial on top. Curves, not straight sides — the swell is
 * the whole point. Its foot OVERHANGS the gallery below it, which is what keeps
 * it reading as a cap and not as the neck of a bottle.
 */
export const MAIDEN_CAP = `M${ SX - 18 },40.2`
  + ` C${ SX - 18 },30.5 ${ SX - 8 },33 ${ SX - 7 },28.6`
  + ` L${ SX + 7 },28.6`
  + ` C${ SX + 8 },33 ${ SX + 18 },30.5 ${ SX + 18 },40.2 Z`
  + ` M${ SX - 7.6 },29.2 L${ SX - 7.6 },23.6 Q${ SX - 7.6 },21.8 ${ SX - 5.6 },21.8`
  + ` L${ SX + 5.6 },21.8 Q${ SX + 7.6 },21.8 ${ SX + 7.6 },23.6 L${ SX + 7.6 },29.2 Z`;

/** The flagpole. Fatter than the icon's hairline: at 22px a 3-unit pole is half
 *  a pixel and the flag ends up floating in the air above the cap. */
export const MAIDEN_POLE = box( SX - 2, 9, SX + 2, 23.5 );

/** The flag, flying right off the top of the pole. */
export const MAIDEN_FLAG = box( SX + 1.4, 9, SX + 14.5, 18.5 );

/**
 * The windows: the row of arched lights under the gallery cornice, one slit
 * mid-shaft and the base's own row. ON only — they are ink on stone, and the OFF
 * silhouette is one flat tone with nothing cut out of it.
 */
export const MAIDEN_WINDOWS = [
  box( SX - 10.8, 42.5, SX - 8.2, 48 ),
  box( SX - 5.2, 42.5, SX - 2.6, 48 ),
  box( SX + 2.6, 42.5, SX + 5.2, 48 ),
  box( SX + 8.2, 42.5, SX + 10.8, 48 ),
  box( SX - 2, 66, SX + 2, 73 ),
  box( 23, 97, 27, 104 ),
  box( 32, 97, 36, 104 ),
  box( 46, 96, 51, GROUND ),
  box( 62, 97, 66, 104 ),
  box( 79, 97, 83, 104 ),
].join( ' ' );

/**
 * The whole tower as ONE path, for the OFF state and for anything that wants a
 * single silhouette. Every subpath is clockwise, so a nonzero fill unions them.
 */
export const MAIDEN_SOLID = [
  MAIDEN_FLAG,
  MAIDEN_POLE,
  MAIDEN_CAP,
  MAIDEN_GALLERY,
  MAIDEN_COLLAR,
  MAIDEN_SHAFT,
  MAIDEN_ROOF,
  MAIDEN_BASE,
].join( ' ' );

/* ----------------------------------------------------------------- colours --
 * Istanbul's LINE skin is a LIGHT one: the glass is near-white #F7FEFF and the
 * classic strip is the bright cyan #4AC3E0. One set of tones has to read on
 * both, which rules out the icon's own cream (#D5C3AB is 1.7:1 on the glass —
 * the shaft would simply not be there).
 */

/** Sunlit stone: the icon's cream pulled down to a tone that still reads on the
 *  near-white glass. 2.64:1 there; on the cyan strip it separates by hue, warm
 *  against cold, the same trade Miami's tab colours already run on. */
export const MAIDEN_STONE = '#B49A6C';
/** Midnight Strait — the cap, the collar and the window slots. 12:1 on the
 *  glass, 6:1 on the strip: the anchor that makes the tower legible on both. */
export const MAIDEN_INK = '#083A41';
/** Rooftop Red — the palette entry named for exactly this roof. */
export const MAIDEN_ROOF_TILE = '#B8514D';
/** Deep Kilim for the flag: a brighter red than the tiles, so the speck at the
 *  top of the pole stays a flag and not a chip off the roof. */
export const MAIDEN_FLAG_RED = '#CA4236';

/**
 * The idle tone: the whole tower flat in one muted Bosphorus teal.
 *
 * Measured on both surfaces it lands on — 4.42:1 on the near-white glass and
 * 2.19:1 on the cyan strip. The lighter teals tried first (#5D8E9A, #6A97A2)
 * looked right on the glass and then dissolved into the strip at 1.75:1 and
 * 1.55:1, which is the same trap Roraima's Dry Grass fell into: an idle tone
 * has to clear the surface it is DIMMEST against, not the one it was picked on.
 * Dusty, desaturated and a shade darker than the theme's own accent, so it
 * still reads as the unlit state next to the lit tower's stone and ink.
 */
export const MAIDEN_OFF = '#4A7E8C';
